var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import { createSelector } from 'reselect';
import { createMaybeSelector } from '@ally/data-selectors';
import { getDomainData } from '@ally/data-selectors-customization';
import * as accounts from './base';
import { checkOwnershipType, filterExpiredAccount, filterByRetirementIndicator, formatDepositAccountsForDropdown, getByType, } from './etc/utils';
import { cdProductCodes, inactiveAccountStatus } from './etc/consts';
/**
 * This selector utility returns a selector that takes accounts.getDepositAll as
 * its first selector and uses the combiner you pass to transform the data
 */
var createDepositSelector = function (combiner) {
    return createMaybeSelector(accounts.getDeposit, combiner);
};
var getIsDepositAccountsUser = createDepositSelector(function (accountSummary) {
    return !!accountSummary.length;
});
var getHasActiveDepositAccounts = createDepositSelector(function (accountSummary) {
    var activeAccounts = accountSummary.filter(filterExpiredAccount);
    return !!activeAccounts.length;
});
var getByAccountId = function (acctId) {
    return function (accountSummary) {
        return accountSummary.filter(function (_a) {
            var accountId = _a.accountId;
            return acctId === accountId;
        });
    };
};
var getByAccountNumber = function (accountNumber) {
    return function (accountSummary) {
        return accountSummary.filter(function (_a) {
            var accountNumberPvtEncrypt = _a.accountNumberPvtEncrypt;
            return accountNumber === accountNumberPvtEncrypt;
        });
    };
};
var getDepositAccountsByAccountNumber = function (accountNumber) {
    return createDepositSelector(getByAccountNumber(accountNumber));
};
var getDepositAccountsByAccountId = function (acctId) {
    return createDepositSelector(getByAccountId(acctId));
};
var getRetirementAccounts = createDepositSelector(function (accountSummary) {
    return accountSummary.filter(filterByRetirementIndicator(true));
});
var getNonRetirementAccounts = createDepositSelector(function (accountSummary) {
    return accountSummary.filter(filterByRetirementIndicator(false));
});
// pass one or multiple DepositAccountTypes. Will return true if all
// deposit accounts are of at least one of the types passed as a param
var getHasOnlyByAccountType = function () {
    var accountTypes = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        accountTypes[_i] = arguments[_i];
    }
    return createDepositSelector(function (accountSummary) {
        return accountSummary
            .filter(filterExpiredAccount)
            .every(function (_a) {
            var accountType = _a.accountType;
            return accountTypes.includes(accountType);
        });
    });
};
var getDepositAccountsByType = function () {
    var accTypes = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        accTypes[_i] = arguments[_i];
    }
    return createDepositSelector(getByType.apply(void 0, __spread(accTypes)));
};
var getRetirementAccountsByType = function (accType) {
    return createMaybeSelector(getRetirementAccounts, getByType(accType));
};
var getNonRetirementAccountsByType = function (accType) {
    return createMaybeSelector(getNonRetirementAccounts, getByType(accType));
};
var getDDA = getDepositAccountsByType('DDA');
var getMMA = getDepositAccountsByType('MMA');
var getSDA = getDepositAccountsByType('SDA');
var getCMG = getDepositAccountsByType('CMG');
var getCDA = getDepositAccountsByType('CDA');
var getDDARetirement = getRetirementAccountsByType('DDA');
var getMMARetirement = getRetirementAccountsByType('MMA');
var getSDARetirement = getRetirementAccountsByType('SDA');
var getCMGRetirement = getRetirementAccountsByType('CMG');
var getCDARetirement = getRetirementAccountsByType('CDA');
var getDDANonRetirement = getNonRetirementAccountsByType('DDA');
var getMMANonRetirement = getNonRetirementAccountsByType('MMA');
var getSDANonRetirement = getNonRetirementAccountsByType('SDA');
var getCMGNonRetirement = getNonRetirementAccountsByType('CMG');
var getCDANonRetirement = getNonRetirementAccountsByType('CDA');
var getOpenInternalDepositAccounts = createDepositSelector(function (accountSummary) {
    return accountSummary.filter(function (_a) {
        var externalAccountIndicator = _a.externalAccountIndicator, accountStatus = _a.accountStatus;
        return accountStatus !== 'Closed' && externalAccountIndicator === false;
    });
});
var getDebitCardEligibleAccounts = createMaybeSelector(getOpenInternalDepositAccounts, function (accountSummary) {
    return getByType('DDA', 'MMA', 'CMG')(accountSummary);
});
var getOpenInternalNonRetirmentDepositAccounts = createMaybeSelector(getOpenInternalDepositAccounts, function (accountSummary) {
    return accountSummary.filter(filterByRetirementIndicator(false));
});
var getPendingExternalDepositAccounts = createDepositSelector(function (accountSummary) {
    return accountSummary.filter(function (_a) {
        var externalAccountIndicator = _a.externalAccountIndicator, accountStatus = _a.accountStatus;
        return externalAccountIndicator === true && accountStatus === 'Pending';
    });
});
var getManagableCds = createDepositSelector(function (accountSummary) {
    return accountSummary.filter(function (_a) {
        var productId = _a.productId, accountStatus = _a.accountStatus, accountClosingDate = _a.accountClosingDate;
        return (cdProductCodes.includes(productId) &&
            !inactiveAccountStatus.includes(accountStatus) &&
            !accountClosingDate);
    });
});
var getHasTrusteeOnlyDepositAccounts = createDepositSelector(function (accountSummary) {
    return accountSummary.length > 0 && accountSummary.every(checkOwnershipType);
});
var getDepositAccountsForDropdown = createSelector(getDDANonRetirement, getMMANonRetirement, getSDANonRetirement, getCMGNonRetirement, getCDANonRetirement, getRetirementAccounts, getDomainData, function () {
    var allSelectors = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        allSelectors[_i] = arguments[_i];
    }
    return formatDepositAccountsForDropdown.apply(void 0, __spread(allSelectors));
});
export default {
    // all deposit accounts selectors
    getAll: accounts.getDeposit,
    getByAccountId: getDepositAccountsByAccountId,
    getByAccountNumber: getDepositAccountsByAccountNumber,
    getByType: getDepositAccountsByType,
    getHasOnlyByType: getHasOnlyByAccountType,
    getDDA: getDDA,
    getMMA: getMMA,
    getSDA: getSDA,
    getCMG: getCMG,
    getCDA: getCDA,
    getOpenInternal: getOpenInternalDepositAccounts,
    getDebitCardEligible: getDebitCardEligibleAccounts,
    getPendingExternal: getPendingExternalDepositAccounts,
    getManagableCds: getManagableCds,
    getAllForDropdown: getDepositAccountsForDropdown,
    // boolean selectors
    getHasTrusteeOnly: getHasTrusteeOnlyDepositAccounts,
    getIsUser: getIsDepositAccountsUser,
    getIsActive: getHasActiveDepositAccounts,
    // retirement account selectors
    retirement: {
        getAll: getRetirementAccounts,
        getByType: getRetirementAccountsByType,
        getDDA: getDDARetirement,
        getMMA: getMMARetirement,
        getSDA: getSDARetirement,
        getCMG: getCMGRetirement,
        getCDA: getCDARetirement,
    },
    // non-retirement account selectors
    nonRetirement: {
        getAll: getNonRetirementAccounts,
        getByType: getNonRetirementAccountsByType,
        getDDA: getDDANonRetirement,
        getMMA: getMMANonRetirement,
        getSDA: getSDANonRetirement,
        getCMG: getCMGNonRetirement,
        getCDA: getCDANonRetirement,
        getOpenInternal: getOpenInternalNonRetirmentDepositAccounts,
    },
};
//# sourceMappingURL=deposit.js.map